/**
 * pages/SignIn.tsx — phone-number step of the web sign-in flow.
 *
 * Step 1 of a two-URL flow: /signin (this file) → /signin/otp.
 *
 * User picks a country, types the local number, hits "Send code".
 * We call POST /v1/auth/phone/start; on success we route to
 * /signin/otp with the challenge state in `location.state` (also
 * stored in sessionStorage so a hard-refresh of the OTP page can
 * recover). The number is optimistically composed into E.164 via
 * the picker's dial code + `digitsOnly` on the input.
 *
 * States:
 *   * idle            — fresh page, empty phone
 *   * pending         — /phone/start in-flight (button loading)
 *   * error           — /phone/start returned an error, banner shown
 *   * signedOutFor    — the user arrived here because their previous
 *                       session hit the idle or absolute cutoff; a
 *                       "Signed out for security" banner explains why.
 *
 * "Signed out for security" copy is not opinionated about the sign-in
 * method — same banner whether the user was originally signed in via
 * mobile handoff or web-OTP. The AuthContext's `signedOutFor` state
 * drives it.
 */

import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button, Field, ThemeToggle } from "../components";
import {
  CountryPicker,
  digitsOnly,
  toE164,
} from "../components/CountryPicker";
import { useAuth } from "../auth/AuthContext";
import { DEFAULT_COUNTRY, type Country } from "../data/countries";
import { startPhoneOtp } from "../api/session";
import { ApiClientError } from "../api/client";
import styles from "./SignIn.module.css";

/** Persisted between /signin and /signin/otp so a hard refresh of the
 * OTP page can recover the challenge context. Cleared on success or
 * explicit back-to-phone navigation. */
export interface SignInOtpState {
  challenge_id: string;
  phone_e164: string;
  attempts_remaining: number;
  /** ISO-8601 absolute expiry of the challenge (5 min from issue). */
  expires_at: string;
  /** ISO-8601; before this, resend button is disabled with a countdown. */
  resend_available_at: string;
  /** ISO-8601 when the challenge was created (for the "we sent it just
   * now" copy on the OTP page). */
  started_at: string;
}

export const SIGNIN_OTP_STATE_KEY = "aster.web.signin.otp.state";

export function SignIn() {
  const { status, signedOutFor, clearSignedOutReason } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Mode is derived from the URL path: /signin renders sign-in copy
  // and posts to /web/session/otp on OTP entry; /signup renders
  // signup copy and posts to /web/signup. The phone-OTP mechanics
  // are IDENTICAL — the difference is only the terminal endpoint
  // and the destination on success.
  const mode: "signin" | "signup" = location.pathname.startsWith("/signup")
    ? "signup"
    : "signin";

  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY);
  const [numberInput, setNumberInput] = useState("");
  const [pending, setPending] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  // Clear the security banner once the user starts typing again — the
  // banner is context, not a persistent state, and it shouldn't
  // linger over their new attempt.
  useEffect(() => {
    if ((numberInput.length > 0 || country.code !== DEFAULT_COUNTRY.code) && signedOutFor) {
      clearSignedOutReason();
    }
  }, [numberInput, country.code, signedOutFor, clearSignedOutReason]);

  if (status === "signed_in") {
    return <Navigate to="/" replace />;
  }

  const digits = digitsOnly(numberInput);
  const canSubmit = digits.length >= 6 && !pending;   // 6+ digits is the
  //                                                     lower bound of any
  //                                                     E.164 number

  async function onSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    if (!canSubmit) return;
    setPending(true);
    setErrMsg(null);
    try {
      const phoneE164 = toE164(country, digits);
      const ch = await startPhoneOtp(phoneE164);
      const otpState: SignInOtpState = {
        challenge_id: ch.challenge_id,
        phone_e164: ch.phone_e164,
        attempts_remaining: ch.attempts_remaining,
        expires_at: ch.expires_at,
        resend_available_at: ch.resend_available_at,
        started_at: new Date().toISOString(),
      };
      // Persist so a refresh on /signin/otp can resume. sessionStorage
      // is same-origin-only and cleared on tab close — the right
      // scope for a mid-flight verification. Not localStorage: we
      // don't want a stale challenge waiting after the user closes
      // the tab.
      try {
        sessionStorage.setItem(SIGNIN_OTP_STATE_KEY, JSON.stringify(otpState));
      } catch {
        /* private-mode Safari can throw; state still flows via
           navigate() below so the flow works even without storage. */
      }
      navigate(mode === "signup" ? "/signup/otp" : "/signin/otp", { state: otpState });
    } catch (err: unknown) {
      if (err instanceof ApiClientError) {
        // Server delivered a shaped envelope — map the codes we
        // know how to explain, fall back to the message for the rest.
        if (err.code === "rate_limited") {
          const secs =
            typeof err.envelope.retry_after_seconds === "number"
              ? err.envelope.retry_after_seconds
              : null;
          setErrMsg(
            secs
              ? `Too many attempts. Try again in ${Math.ceil(secs / 60)} minute(s).`
              : "Too many attempts. Try again in a few minutes.",
          );
        } else if (err.code === "validation_failed") {
          setErrMsg(
            err.envelope.message ||
              "That phone number doesn't look right. Check the country and try again.",
          );
        } else {
          setErrMsg(err.envelope.message || "Couldn't send code. Try again.");
        }
      } else {
        setErrMsg("Couldn't reach AsterCash. Check your connection.");
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <main className={styles.page}>
      <ThemeToggle />
      <section className={styles.card}>
        {signedOutFor && (
          <div className={styles.securityBanner} role="alert">
            <span className={styles.securityDot} aria-hidden />
            <div>
              <p className={styles.securityTitle}>Signed out for security</p>
              <p className={styles.securityBody}>
                {signedOutFor === "idle"
                  ? "You were inactive for 30 minutes. Sign in again to continue."
                  : "Your session reached its 8-hour lifetime. Sign in again to continue."}
              </p>
            </div>
          </div>
        )}
        <img
          src="/logo.png"
          alt=""
          width={56}
          height={56}
          className={styles.brandMark}
        />
        <h1 className={styles.title}>
          {mode === "signup"
            ? "Create your AsterCash account"
            : signedOutFor
            ? "Welcome back"
            : "Sign in to AsterCash"}
        </h1>
        <p className={styles.body}>
          {mode === "signup"
            ? "We'll text you a 6-digit code to verify your phone. Then we'll walk you through the four-minute account-opening flow."
            : "We'll text you a 6-digit code to sign you in."}
        </p>

        <form className={styles.form} onSubmit={onSubmit} noValidate>
          <div className={styles.phoneRow}>
            <CountryPicker value={country} onChange={setCountry} disabled={pending} />
            <div className={styles.phoneField}>
              <Field
                label="Phone number"
                value={numberInput}
                onChange={setNumberInput}
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                placeholder="(555) 123-4567"
                disabled={pending}
              />
            </div>
          </div>

          {errMsg && (
            <p className={styles.errorLine} role="alert">
              {errMsg}
            </p>
          )}

          <Button
            variant="primary"
            fullWidth
            loading={pending}
            disabled={!canSubmit}
            onClick={() => void onSubmit()}
          >
            {pending ? "Sending code…" : "Send code"}
          </Button>
        </form>

        <p className={styles.subtle}>
          By continuing you accept read-only access on web. Money-moving
          actions still require your mobile device.
        </p>
      </section>
    </main>
  );
}
